import Button from "@/Components/Button/Button";
import DeleteButton from "@/Components/Button/DeleteButton";
import Icon from "@/Components/Icon/Icon";
import useOnClickOutside from "@/shared/hooks/useOnClickOutside";
import { isObject } from "@/shared/utils/helpers";
import {
  cloneElement,
  FormEvent,
  memo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { mapFilters } from "./helpers";
import { TableFiltersContainer } from "./Table.styles";
import {
  TableColumn,
  TableFilterFunctionInput,
  TableFilterValues,
} from "./types";
import useTableFiltersHook from "./useTableFiltersHook";
import { cn } from "@/shared/utils/cn";

interface TableFiltersProps {
  columns: TableColumn[];
  additionalHTML?: any;
  tableName: string;
  disableFilter?: boolean;
  filterRoutes?: string[];
}

const defaultFilters = [];
const TableFilters = ({
  additionalHTML,
  columns,
  tableName,
  disableFilter = false,
  filterRoutes,
}: TableFiltersProps) => {
  const { addFilter, filter } = useTableFiltersHook(tableName, filterRoutes);
  const { t } = useTranslation("", {
    keyPrefix: "orders_archive",
  });
  const [localFilters, setLocalFilters] =
    useState<TableFilterValues[]>(defaultFilters);

  const handleFilterChange = useCallback(
    (value: any, id: string, filter: TableFilterFunctionInput) => {
      const newValues = mapFilters(localFilters, { value, id, filter });
      setLocalFilters(newValues);
    },
    [localFilters]
  );

  const findLocalFilter = (id: string) =>
    localFilters.find((filter) => filter.id === id);

  const [show, setShow] = useState(false);
  const ref = useRef();
  useOnClickOutside(ref, () => show === true && setShow(false));

  const resetFilters = () => {
    setLocalFilters([]);
    addFilter(tableName, []);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    addFilter(tableName, localFilters);
    setShow(false);
  };

  return (
    <div className="mb-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start justify-between">
        <div className="flex-1">{additionalHTML}</div>
        {!disableFilter && (
          <div className="flex" data-tour="table.filters">
            {filter?.filters?.some((f) =>
              Array.isArray(f?.filter)
                ? f?.filter?.length > 0
                : isObject(f?.filter)
                  ? true
                  : false
            ) && <DeleteButton mr="0.5rem" onClick={() => resetFilters()} />}
            <Button onClick={() => setShow(!show)}>{t("filters")}</Button>
          </div>
        )}
      </div>
      {!disableFilter && (
        <div
          className={cn(
            "fixed right-0 top-0 left-0 bottom-0 bg-black/50 z-9999",
            show ? "block" : "hidden"
          )}
          onClick={() => setShow(false)}
        >
          <TableFiltersContainer
            className={cn(
              "fixed right-0 top-0 h-full w-full max-w-180 bg-white z-12 p-12 shadow-primary transition-all duration-750 overflow-y-auto",
              show ? "translate-x-0" : "translate-x-180"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {localFilters.length !== 0 && (
              <div className="mb-[1rem]">
                <Button onClick={resetFilters}>{t("resetFilters")}</Button>
              </div>
            )}
            <div className="flex justify-between">
              <div className="text-[2.5rem] font-semibold">{t("filters")}</div>
              <div
                className="flex cursor-pointer items-center"
                onClick={() => setShow(false)}
              >
                <Icon className="size-10" variants="times" />
              </div>
            </div>
            <form onSubmit={handleSubmit}>
              {columns
                .filter((column) => !column.disable)
                .filter((column) => column.Filter !== undefined)
                .map(({ Header, id, Filter, accessor }) =>
                  Array.isArray(Filter) ? (
                    Filter.map((filter) => (
                      <div key={filter.id || id} className="mt-[2rem]">
                        {cloneElement(filter.Component, {
                          Header: filter?.Header || Header,
                          id: id,
                          filterId: filter.id,
                          value:
                            findLocalFilter(filter.id || id)?.value ||
                            undefined,
                          setValue: (value, filterId) => {
                            return handleFilterChange(
                              value,
                              filterId,
                              filter.filter({
                                accessor,
                                id,
                                filterId,
                                values: value,
                              })
                            );
                          },
                        })}
                      </div>
                    ))
                  ) : (
                    <div key={id} className="mt-[2rem]">
                      {cloneElement(Filter.Component, {
                        Header: Filter?.Header || Header,
                        id,
                        filterId: Filter.id,
                        value: findLocalFilter(id)?.value || undefined,
                        setValue: (value, filterId) =>
                          handleFilterChange(
                            value,
                            filterId,
                            Filter.filter({
                              accessor,
                              id,
                              filterId,
                              values: value,
                            })
                          ),
                      })}
                    </div>
                  )
                )}
              <div className="mt-[2rem]">
                <Button
                  isDisabled={
                    localFilters.length === 0 && filter.filters.length === 0
                  }
                >
                  {t("save")}
                </Button>
              </div>
            </form>
          </TableFiltersContainer>
        </div>
      )}
    </div>
  );
};

export default memo(TableFilters);
